import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProgressChart from '../../Portfolio/CardIndicator/Cards/ProgressChart/ProgressChart';
import GcmChart from './GcmChart/GcmChart';
import styles from './GcmAlert.module.scss';
import GcmTable from './GcmTable/GcmTable';
import { getProjectsSimpleTableIndicator } from '../../../redux/actions/indicatorProjects';
import { GcmStatus } from '../../../models/GcmAlerts';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { translateMultipleText } from '../../../services/stringUtil';
import { useTranslation } from 'react-i18next';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import FilterIndicators from '../../../models/FilterIndicators';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { arrayMove } from 'react-sortable-hoc';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { addIndicators } from '../../../redux/actions/indicators';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import Filter from '../../Portfolio/Filter/Filter';
import SpinningLoader from '../../ProjectDetail/ProjectTabs/Loader/SpinningLoader';

type Props = {
  currentIndicator: any;
};

const GcmAlert = ({ currentIndicator }: Props) => {
  const { container, multiChartContainer, chartTableContainer } = styles;
  const [status, setStatus] = useState<GcmStatus | null>(null);
  const [list, setList] = useState<any>(
    currentIndicator?.data?.chartData ? [...currentIndicator?.data?.chartData] : [],
  );
  const [currentChart, setCurrentChart] = useState<any>(null);
  const [currentBar, setCurrentBar] = useState<any>(null);
  const [currentMapperKey, setCurrentMapperKey] = useState<string>('');
  const indicatorProjects = useSelector<any, any[]>(state => state.indicatorProjects);
  const dispatch = useDispatch();
  const alertTypeId = 'alertTypeId';
  const { t } = useTranslation();

  // Filter
  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = React.useState(false);
  const [totalProjectsData, setTotalProjectsData] = React.useState(false);
  const [flag, setFlag] = useState<boolean>(false);

  useEffect(() => {
    setList(currentIndicator?.data?.chartData ? [...currentIndicator?.data?.chartData] : []);
    setStatus(null);
  }, [currentIndicator]);

  const projectTypeFilterChange = {
    ...filterIndicators,
    common: { ...filterIndicators.common, projectTypeFilters: stateProjectProductTypes },
    projectTypeFilters: stateProjectProductTypes,
  };

  const handleCommonFilterChange = async () => {
    dispatch(setLoading(true));
    setIsLoading(true);

    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters,
      );
      setTotalProjectsData(emptyProjects(indicatorsToProcess.totalProjects));
    } catch (err) {
      console.error(err, 'Error fetching data from indicators');
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    dispatch(setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'GCM_ALERTS')));
    setIsLoading(false);
  };

  // const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  //   menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

  useEffect(() => {
    sessionStorage.removeItem(alertTypeId);
    return () => {
      sessionStorage.removeItem(alertTypeId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0
    )
      handleCommonFilterChange();
    setFlag(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicators.common.coReponsibleDivisions.length === 0 &&
      filterIndicators.common.countryId.length === 0 &&
      filterIndicators.common.divisionId.length === 0 &&
      filterIndicators.common.interestIssues.length === 0 &&
      filterIndicators.common.participantDivisions.length === 0 &&
      filterIndicators.common.regionId.length === 0 &&
      filterIndicators.common.sectors.length === 0 &&
      filterIndicators.common.teamLeaders.length === 0 &&
      flag
    ) {
      handleCommonFilterChange();
      setFlag(false);
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  const setAlertTypeIdInStorageUpdateCurrentMapperKey = (cChart, item, projectTypeFilterChange) => {
    if (cChart && cChart.dataList && cChart.dataList.length && item && item[0]) {
      sessionStorage.setItem(alertTypeId, cChart.dataList[item[0]._index].id);
      setCurrentMapperKey(cChart.dataList[item[0]._index].mapperKey);
    }
    dispatch(getProjectsSimpleTableIndicator(currentIndicator.id, undefined, projectTypeFilterChange));
  };

  const handleStatusChange = (newStatus: number, projectTypeFilterChange) => {
    setStatus(newStatus);
    const cChart = list.find(i => i.status === newStatus);
    setCurrentChart(cChart || null);
    setCurrentBar(null);
    setAlertTypeIdInStorageUpdateCurrentMapperKey(cChart, { 0: { _index: 0 } }, projectTypeFilterChange);
  };

  const setCurrentChartAndTable = (status: GcmStatus, item: any, projectTypeFilterChange) => {
    setStatus(status);
    const cChart = list.find(i => i.status === status);
    setCurrentChart(cChart || null);
    setCurrentBar(item);
    setAlertTypeIdInStorageUpdateCurrentMapperKey(cChart, item, projectTypeFilterChange);
  };

  const handleClickChart = (item, projectTypeFilterChange) => {
    setCurrentBar(item);
    setAlertTypeIdInStorageUpdateCurrentMapperKey(currentChart, item, projectTypeFilterChange);
  };

  return (
    <>
      <div className={container}>
        <ProgressChart
          data={currentIndicator.data && currentIndicator.data.progressData ? currentIndicator.data.progressData : null}
          isDetail={true}
          handleClick={value => {
            handleStatusChange(value, projectTypeFilterChange);
          }}
          indicatorKey={currentIndicator.indicatorKey}
        />
        {!isLoading ? (
          status === null ? (
            <div className={multiChartContainer}>
              {list.map(item => (
                <GcmChart
                  data={item.data}
                  title={item.title}
                  key={item.status}
                  handleClick={element => setCurrentChartAndTable(item.status, element, projectTypeFilterChange)}
                />
              ))}
            </div>
          ) : (
            <div className={chartTableContainer} key={status}>
              <GcmChart
                data={currentChart.data}
                title={currentChart.title || ''}
                handleClick={item => {
                  handleClickChart(item, projectTypeFilterChange);
                }}
              />
              <GcmTable
                currentMapperKey={currentMapperKey}
                projects={indicatorProjects}
                status={status}
                title={
                  currentBar &&
                  currentBar.length &&
                  currentBar[0]._model &&
                  currentBar[0]._model.label &&
                  currentBar[0]._model.label.length
                    ? currentBar[0]._model.label.join(' ')
                    : list &&
                      list.length &&
                      list[status].dataList &&
                      list[status].dataList.length &&
                      list[status].dataList[0].label
                }
              />
            </div>
          )
        ) : (
          <SpinningLoader customStyle={{ margin: '85px auto' }} />
        )}
      </div>
    </>
  );
};

export default GcmAlert;
